<template>
  <div id="outsideContainer" class="border word-cloze-page">
    <div class="set-location card-header">
      <div class="inside-header-left">
        <span class="andover-logo-area" @click="isAssignmentSession ? goAssignmentList() : goHome()">
          <img style="width: 20px; height: 20px" src="../andover.png" alt="home" />
        </span>
      </div>
      <div class="navi-text">
        <span>{{ payload?.subjectName || '选词填空' }}</span>
        <span v-if="payload?.setName"> &gt; {{ payload.setName }}</span>
      </div>
    </div>

    <div class="card-wrapper">
      <div class="card-content cloze-content">
        <div v-if="loadError" class="cloze-panel">
          <p>{{ loadError }}</p>
          <el-button type="primary" @click="goHome">返回首页</el-button>
        </div>

        <div v-else-if="assignmentLoading" class="cloze-panel" v-loading="true">
          <p>加载选词填空作业...</p>
        </div>

        <div v-else-if="phase === 'intro'" class="cloze-panel">
          <h2>选词填空</h2>
          <p class="intro-desc">
            将上方单词拖入例句挖空处。每组 {{ groupSize }} 个词，共
            {{ groups.length }} 组。
          </p>
          <div class="intro-notice">
            <p><strong>练习说明</strong></p>
            <ul>
              <li>
                填入时，选项中的单词<strong>不会</strong>随词性变化（如 speed → speeding）或单复数变化（如 language → languages）而改变，始终显示原词。
              </li>
              <li>
                提交后，例句中发生变化的词形部分会以<span class="intro-red-sample">红字</span>显示，便于核对是否与自己预想的一致。
              </li>
            </ul>
          </div>
          <div class="intro-config-card">
            <div class="intro-config-grid">
              <div class="intro-stat-item">
                <span class="intro-stat-value">{{ payload?.items?.length || 0 }}</span>
                <span class="intro-stat-label">带例句词汇</span>
              </div>
              <div class="intro-stat-divider" aria-hidden="true"></div>
              <div class="intro-stat-item">
                <span class="intro-stat-value">{{ exerciseItemCount }}</span>
                <span class="intro-stat-label">练习题数</span>
              </div>
              <div class="intro-stat-divider" aria-hidden="true"></div>
              <div class="intro-stat-item">
                <span class="intro-stat-value">{{ groups.length }}</span>
                <span class="intro-stat-label">练习组数</span>
              </div>
            </div>
            <div class="intro-time-row">
              <div class="intro-time-label-shell">
                <span class="intro-time-badge">
                  <el-icon :size="16"><Timer /></el-icon>
                </span>
                <div class="intro-time-text">
                  <span class="intro-time-title">每组限时</span>
                </div>
              </div>
              <div class="intro-time-input-shell">
                <template v-if="isAssignmentSession">
                  <span class="intro-time-field intro-time-locked">{{ timeLimitSeconds }}</span>
                  <span class="intro-time-suffix">秒</span>
                </template>
                <template v-else>
                  <input
                    v-model.number="timeLimitSeconds"
                    class="intro-time-field"
                    type="number"
                    step="5"
                  />
                  <div class="intro-time-stepper">
                    <button
                      type="button"
                      class="intro-time-step-btn"
                      aria-label="增加秒数"
                      @click="adjustTimeLimit(5)"
                    >
                      +
                    </button>
                    <button
                      type="button"
                      class="intro-time-step-btn"
                      aria-label="减少秒数"
                      @click="adjustTimeLimit(-5)"
                    >
                      −
                    </button>
                  </div>
                  <span class="intro-time-suffix">秒</span>
                </template>
              </div>
            </div>
          </div>
          <el-button type="primary" size="large" class="intro-start-btn" @click="startExercise">
            开始练习
          </el-button>
        </div>

        <div v-else-if="phase === 'summary'" class="summary-page">
          <div class="summary-header">
            <h2>练习完成</h2>
            <p class="summary-score">
              正确 {{ totalCorrect }} / {{ totalQuestions }}（{{ accuracyPercent }}%）
            </p>
            <p class="summary-time">总用时：{{ formatDuration(totalElapsedSeconds) }}</p>
          </div>

          <section
            v-for="(group, groupIndex) in groups"
            :key="'summary-group-' + groupIndex"
            class="summary-group-section"
          >
            <h3 class="summary-group-title">
              第 {{ groupIndex + 1 }} 组
              <span class="summary-group-score">{{ groupResultLabel(groupIndex) }}</span>
            </h3>

            <div
              v-for="slot in group.slots"
              :key="slot.itemKey"
              class="sentence-row"
              :class="summaryRowClass(slot)"
            >
              <p class="slot-word-label">
                <span class="slot-base-word">{{ slot.baseWord }}</span>
              </p>
              <p class="sentence-line">
                <span>{{ slot.sentenceBefore }}</span>
                <span
                  class="cloze-blank"
                  :class="summaryBlankClass(slot)"
                >
                  <span
                    v-if="summaryShowFormChange(slot)"
                    class="blank-filled-word blank-result-form"
                  >
                    <span
                      v-for="(seg, segIndex) in wordFormSegments(slot)"
                      :key="segIndex"
                      :class="{ 'form-change-part': seg.changed }"
                    >{{ seg.text }}</span>
                  </span>
                  <span
                    v-else-if="summaryBlankDisplayText(slot, group)"
                    class="blank-filled-word blank-result-form"
                  >
                    {{ summaryBlankDisplayText(slot, group) }}
                  </span>
                  <span v-else class="blank-placeholder">______</span>
                </span>
                <span>{{ slot.sentenceAfter }}</span>
              </p>

              <div class="result-detail">
                <el-icon v-if="slot.isCorrect" class="icon-correct"><CircleCheckFilled /></el-icon>
                <template v-else>
                  <el-icon class="icon-wrong"><CircleCloseFilled /></el-icon>
                  <span class="correct-answer-hint">
                    正确答案：<strong>{{ slot.baseWord }}</strong>
                  </span>
                </template>
              </div>
            </div>
          </section>

          <div class="summary-actions">
            <template v-if="isAssignmentSession">
              <el-button type="primary" @click="goAssignmentList">返回作业列表</el-button>
              <el-button
                type="warning"
                :disabled="!hasWrongItems || assigningNirvana"
                :loading="assigningNirvana"
                @click="assignNirvanaFromWrongItems"
              >
                Nirvana
              </el-button>
            </template>
            <template v-else>
              <el-button type="primary" @click="restartExercise">再来一遍</el-button>
              <el-button type="warning" :disabled="!hasWrongItems" @click="startNirvana">
                Nirvana
              </el-button>
              <el-button @click="goHome">返回</el-button>
            </template>
          </div>
        </div>

        <template v-else>
          <div class="cloze-toolbar">
            <span class="group-label">
              <template v-if="isNirvanaSession">Nirvana · </template>
              第 {{ currentGroupIndex + 1 }} / {{ groups.length }} 组
            </span>
            <div class="timer-row">
              <span>剩余时间</span>
              <voc-count-down
                ref="vocCountDown"
                @on-count-down-finished="handleTimeUp"
              />
            </div>
          </div>

          <div v-if="phase === 'groupResult'" class="result-banner">
            本组正确 {{ currentGroupCorrect }} / {{ currentGroup.slots.length }}
          </div>

          <section class="word-bank-section">
            <h3>单词选项</h3>
            <div class="word-bank">
              <div
                v-for="word in currentGroup.wordBank"
                :key="word.wordKey"
                class="word-chip"
                :class="{
                  'word-chip-used': word.placedSlotIndex !== null && phase !== 'groupResult',
                  'word-chip-hidden': word.placedSlotIndex !== null && phase === 'exercise'
                }"
                :draggable="canDragWord()"
                @dragstart="onWordDragStart(word, $event)"
                @dragend="onDragEnd"
                @click="onWordClick(word)"
              >
                {{ word.baseWord }}
              </div>
            </div>
            <p v-if="selectedWordKey && phase === 'exercise'" class="click-hint">
              已选中「{{ selectedWordLabel }}」，点击例句挖空处填入
            </p>
          </section>

          <section class="sentences-section">
            <h3>例句</h3>
            <div
              v-for="slot in currentGroup.slots"
              :key="slot.itemKey"
              class="sentence-row"
              :class="resultRowClass(slot)"
            >
              <p class="sentence-line">
                <span>{{ slot.sentenceBefore }}</span>
                <span
                  class="cloze-blank"
                  :class="blankClass(slot)"
                  @dragover.prevent="onBlankDragOver"
                  @drop.prevent="onBlankDrop(slot, $event)"
                  @click="onBlankClick(slot)"
                >
                  <span
                    v-if="phase === 'groupResult' && slot.isCorrect && hasFormChange(slot)"
                    class="blank-filled-word blank-result-form"
                  >
                    <span
                      v-for="(seg, segIndex) in wordFormSegments(slot)"
                      :key="segIndex"
                      :class="{ 'form-change-part': seg.changed }"
                    >{{ seg.text }}</span>
                  </span>
                  <span
                    v-else-if="blankDisplayText(slot)"
                    class="blank-filled-word"
                    :class="{ 'blank-result-form': phase === 'groupResult' }"
                    :draggable="phase === 'exercise'"
                    @dragstart="onBlankWordDragStart(slot, $event)"
                    @dragend="onDragEnd"
                  >
                    {{ blankDisplayText(slot) }}
                  </span>
                  <span v-else class="blank-placeholder">______</span>
                </span>
                <span>{{ slot.sentenceAfter }}</span>
              </p>

              <div v-if="phase === 'groupResult'" class="result-detail">
                <el-icon v-if="slot.isCorrect" class="icon-correct"><CircleCheckFilled /></el-icon>
                <template v-else>
                  <el-icon class="icon-wrong"><CircleCloseFilled /></el-icon>
                  <span class="correct-answer-hint">
                    正确答案：<strong>{{ slot.baseWord }}</strong>
                  </span>
                </template>
              </div>
            </div>
          </section>

          <div class="cloze-actions">
            <el-button
              v-if="phase === 'exercise'"
              type="primary"
              size="large"
              :disabled="!allSlotsFilled"
              @click="submitCurrentGroup"
            >
              提交本组
            </el-button>
            <el-button
              v-else-if="phase === 'groupResult'"
              type="primary"
              size="large"
              @click="goNextGroup"
            >
              {{ isLastGroup ? '查看总结果' : '下一组' }}
            </el-button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import VocCountDown from '../recordtotest/VocCountDown.vue'
import Tags from '../tags'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  VOC_WORD_CLOZE_STORAGE_KEY,
  DEFAULT_CLOZE_TIME_LIMIT_SECONDS,
  DIGEST_EXAMPLE_SENTENCE_TAG_ID,
  appendPendingClozeTag,
  buildClozeGroups,
  diffWordFormSegments,
  getPrimaryBaseWord,
  buildVocStudentClozeTestPayload,
  buildVocClozeAssignmentResultPayload,
  buildVocClozeAssignmentPayload,
  normalizeAssignmentGroups,
  itemsFromAssignmentGroups,
  isCompletionType,
  collectWrongItemsFromGroups,
  nirvanaSetName
} from './wordClozeUtils'

export default {
  name: 'WordCloze',
  components: { VocCountDown },

  data () {
    return {
      payload: null,
      loadError: '',
      groups: [],
      groupSize: 5,
      timeLimitSeconds: DEFAULT_CLOZE_TIME_LIMIT_SECONDS,
      phase: 'intro',
      currentGroupIndex: 0,
      draggingWordKey: null,
      draggingFromSlotIndex: null,
      selectedWordKey: null,
      groupResults: [],
      totalElapsedSeconds: 0,
      groupStartedAt: null,
      activeItems: null,
      isNirvanaSession: false,
      taggedWrongVocabularyIds: new Set(),
      clozeTestSubmitted: false,
      assignmentSourceGroups: [],
      assignmentLoading: false,
      assigningNirvana: false
    }
  },

  computed: {
    practiceItems () {
      return this.activeItems || this.payload?.items || []
    },
    hasWrongItems () {
      return this.collectWrongItems().length > 0
    },
    currentGroup () {
      return this.groups[this.currentGroupIndex] || { slots: [], wordBank: [] }
    },
    allSlotsFilled () {
      return this.currentGroup.slots.every((slot) => slot.assignedWordKey)
    },
    isLastGroup () {
      return this.currentGroupIndex >= this.groups.length - 1
    },
    currentGroupCorrect () {
      return this.currentGroup.slots.filter((slot) => slot.isCorrect).length
    },
    totalCorrect () {
      return this.groupResults.reduce((sum, group) => sum + group.correct, 0)
    },
    totalQuestions () {
      return this.groupResults.reduce((sum, group) => sum + group.total, 0)
    },
    exerciseItemCount () {
      return this.groups.reduce((sum, group) => sum + group.slots.length, 0)
    },
    accuracyPercent () {
      if (!this.totalQuestions) return 0
      return Math.round((this.totalCorrect / this.totalQuestions) * 100)
    },
    selectedWordLabel () {
      const word = this.currentGroup.wordBank.find((w) => w.wordKey === this.selectedWordKey)
      return word ? word.baseWord : ''
    },
    isAssignmentSession () {
      return Number(this.payload?.assignmentId) > 0
    }
  },

  mounted () {
    this.loadPayload()
  },

  beforeUnmount () {
    this.stopTimer()
  },

  methods: {
    loadPayload () {
      const assignmentId = Number(this.$route.query.assignmentId)
      if (Number.isFinite(assignmentId) && assignmentId > 0) {
        this.loadAssignment(assignmentId)
        return
      }
      try {
        const raw = sessionStorage.getItem(VOC_WORD_CLOZE_STORAGE_KEY)
        if (!raw) {
          this.loadError = '未找到词汇数据，请从单词卡页面进入选词填空。'
          return
        }
        this.payload = JSON.parse(raw)
        this.assignmentSourceGroups = []
        // 每组限时在练习开始前由用户设置，不沿用 sessionStorage 里可能过期的旧值
        this.timeLimitSeconds = DEFAULT_CLOZE_TIME_LIMIT_SECONDS
        this.groups = buildClozeGroups(this.payload.items || [], this.groupSize)
        if (!this.groups.length) {
          this.loadError = '没有可在例句中匹配到的词汇，请确认单词和例句是否对应。'
        }
      } catch (e) {
        this.loadError = '加载词汇数据失败，请从单词卡页面重新进入。'
      }
    },

    loadAssignment (assignmentId) {
      this.assignmentLoading = true
      this.$store
        .dispatch('VocClozeAssignment/getById', assignmentId)
        .then((response) => {
          const assignment = response?.data
          if (!assignment) {
            this.loadError = '未找到该选词填空作业。'
            return
          }
          if (isCompletionType(assignment)) {
            this.loadError = '该作业是完形填空，请从 Blank HW 进入。'
            return
          }
          const groups = normalizeAssignmentGroups(assignment.groups)
          this.assignmentSourceGroups = groups
          this.groups = JSON.parse(JSON.stringify(groups))
          this.groupSize = assignment.groupSize || 5
          this.timeLimitSeconds =
            Number(assignment.timeLimitSeconds) || DEFAULT_CLOZE_TIME_LIMIT_SECONDS
          this.payload = {
            assignmentId: assignment.id,
            studentId: assignment.studentId,
            subjectName: assignment.subjectName || '',
            setName: assignment.setName || '',
            type: assignment.type || '选词填空',
            items: itemsFromAssignmentGroups(groups)
          }
          if (!this.groups.length) {
            this.loadError = '该作业没有可练习的题目。'
          }
        })
        .catch((err) => {
          console.error(err)
          const d = err.response?.data
          this.loadError =
            (typeof d === 'string' && d) ||
            d?.message ||
            d?.title ||
            err.message ||
            '加载选词填空作业失败'
        })
        .finally(() => {
          this.assignmentLoading = false
        })
    },

    resetGroupsForSession () {
      if (this.isAssignmentSession && this.assignmentSourceGroups.length) {
        this.groups = JSON.parse(JSON.stringify(this.assignmentSourceGroups))
        return
      }
      this.groups = buildClozeGroups(this.practiceItems, this.groupSize)
    },

    startExercise () {
      this.phase = 'exercise'
      this.currentGroupIndex = 0
      this.groupResults = []
      this.totalElapsedSeconds = 0
      this.isNirvanaSession = false
      this.activeItems = null
      this.taggedWrongVocabularyIds = new Set()
      this.clozeTestSubmitted = false
      this.resetGroupsForSession()
      this.prepareGroup()
    },

    restartExercise () {
      this.resetGroupsForSession()
      this.phase = 'exercise'
      this.currentGroupIndex = 0
      this.groupResults = []
      this.totalElapsedSeconds = 0
      this.clozeTestSubmitted = false
      this.prepareGroup()
    },

    startNirvana () {
      const wrongItems = this.collectWrongItems()
      if (!wrongItems.length) {
        ElMessage({
          message: '全部正确，无需 Nirvana',
          type: 'success'
        })
        return
      }
      this.activeItems = wrongItems
      this.isNirvanaSession = true
      this.groups = buildClozeGroups(wrongItems, this.groupSize)
      this.phase = 'exercise'
      this.currentGroupIndex = 0
      this.groupResults = []
      this.totalElapsedSeconds = 0
      this.clozeTestSubmitted = false
      this.prepareGroup()
    },

    collectWrongItems () {
      return collectWrongItemsFromGroups(this.groups)
    },

    assignNirvanaFromWrongItems () {
      const wrongItems = this.collectWrongItems()
      if (!wrongItems.length) {
        ElMessage({ message: '全部正确，无需 Nirvana', type: 'success' })
        return
      }
      const groups = buildClozeGroups(wrongItems, this.groupSize)
      const questionCount = groups.reduce((sum, group) => sum + group.slots.length, 0)
      const studentId =
        Number(this.payload?.studentId) ||
        Number(localStorage.getItem('targetId')) ||
        Number(localStorage.getItem('userId')) ||
        0
      const teacherId = Number(localStorage.getItem('userId')) || studentId
      const subjectName = this.payload?.subjectName || ''
      const setName = nirvanaSetName(this.payload?.setName)

      ElMessageBox.prompt(
        `将本次 ${questionCount} 道错题重新布置为 Nirvana（共 ${groups.length} 组）。请填写每组限时（秒）。`,
        'Nirvana',
        {
          confirmButtonText: '布置',
          cancelButtonText: '取消',
          inputValue: String(this.timeLimitSeconds || DEFAULT_CLOZE_TIME_LIMIT_SECONDS),
          inputPlaceholder: '每组限时（秒）',
          inputType: 'number',
          inputValidator: (value) => {
            const seconds = Number(value)
            if (!Number.isFinite(seconds) || seconds < 1) {
              return '请输入每组限时，至少 1 秒'
            }
            return true
          }
        }
      )
        .then(({ value }) => {
          this.assigningNirvana = true
          const timeLimitSeconds = Math.max(1, Math.round(Number(value)))
          const postData = buildVocClozeAssignmentPayload({
            payload: {
              subjectName,
              setName,
              type: '选词填空'
            },
            groups,
            studentId,
            teacherId,
            groupSize: this.groupSize,
            timeLimitSeconds
          })
          return this.$store.dispatch('VocClozeAssignment/add', postData)
        })
        .then((response) => {
          if (!response) return
          const assignmentId = Number(response?.data?.id ?? response?.data)
          ElMessage.success(
            Number.isFinite(assignmentId) && assignmentId > 0
              ? `Nirvana 作业已布置（ID ${assignmentId}）`
              : 'Nirvana 作业已布置'
          )
          if (Number.isFinite(assignmentId) && assignmentId > 0 && this.isStudentUser()) {
            this.$router.push({
              path: '/wordCloze',
              query: { assignmentId: String(assignmentId) }
            })
            return
          }
          this.goAssignmentList()
        })
        .catch((err) => {
          if (err === 'cancel' || err === 'close') return
          console.error(err)
          const d = err.response?.data
          const firstValidationError = d?.errors
            ? Object.values(d.errors).find((arr) => Array.isArray(arr) && arr.length)?.[0]
            : ''
          const msg =
            (typeof d === 'string' && d) ||
            firstValidationError ||
            d?.message ||
            d?.title ||
            err.message ||
            '布置 Nirvana 作业失败'
          ElMessage.error(String(msg))
        })
        .finally(() => {
          this.assigningNirvana = false
        })
    },

    isStudentUser () {
      return (localStorage.getItem('userRole') || '') === 'Student'
    },

    prepareGroup () {
      this.selectedWordKey = null
      this.draggingWordKey = null
      this.draggingFromSlotIndex = null
      this.groupStartedAt = Date.now()
      this.$nextTick(() => {
        this.startTimer()
      })
    },

    startTimer () {
      const seconds = Math.max(1, Number(this.timeLimitSeconds) || DEFAULT_CLOZE_TIME_LIMIT_SECONDS)
      this.timeLimitSeconds = seconds
      if (this.$refs.vocCountDown) {
        this.$refs.vocCountDown.countdown(seconds, true)
      }
    },

    adjustTimeLimit (delta) {
      const current = Number(this.timeLimitSeconds)
      const base = Number.isFinite(current) ? current : DEFAULT_CLOZE_TIME_LIMIT_SECONDS
      this.timeLimitSeconds = base + delta
    },

    stopTimer () {
      if (this.$refs.vocCountDown) {
        this.$refs.vocCountDown.countdown(0, false)
      }
    },

    recordGroupElapsed () {
      if (!this.groupStartedAt) return 0
      const elapsed = Math.max(0, Math.round((Date.now() - this.groupStartedAt) / 1000))
      this.totalElapsedSeconds += elapsed
      this.groupStartedAt = null
      return elapsed
    },

    handleTimeUp () {
      if (this.phase === 'exercise') {
        this.submitCurrentGroup(true)
      }
    },

    canDragWord () {
      return this.phase === 'exercise'
    },

    placedWordForSlot (slot, group = this.currentGroup) {
      if (!slot.assignedWordKey) return null
      return group.wordBank.find((w) => w.wordKey === slot.assignedWordKey) || null
    },

    summaryBlankDisplayText (slot, group) {
      const word = this.placedWordForSlot(slot, group)
      if (slot.isCorrect) {
        return slot.correctForm
      }
      return word ? word.baseWord : null
    },

    summaryShowFormChange (slot) {
      return slot.isCorrect && this.hasFormChange(slot)
    },

    summaryBlankClass (slot) {
      return {
        'blank-filled': !!slot.assignedWordKey || slot.isCorrect,
        'blank-correct': slot.isCorrect,
        'blank-wrong': !slot.isCorrect
      }
    },

    summaryRowClass (slot) {
      return {
        'row-correct': slot.isCorrect,
        'row-wrong': !slot.isCorrect
      }
    },

    groupResultLabel (groupIndex) {
      const result = this.groupResults.find((item) => item.groupIndex === groupIndex)
      if (!result) return ''
      return `${result.correct} / ${result.total} 正确`
    },

    blankDisplayText (slot) {
      const word = this.placedWordForSlot(slot)
      if (this.phase === 'groupResult') {
        if (slot.isCorrect) {
          return slot.correctForm
        }
        return word ? word.baseWord : null
      }
      return word ? word.baseWord : null
    },

    hasFormChange (slot) {
      const primary = slot.baseWord.split(/[/|(]/)[0].trim()
      return primary.toLowerCase() !== slot.correctForm.toLowerCase()
    },

    wordFormSegments (slot) {
      return diffWordFormSegments(slot.baseWord, slot.correctForm)
    },

    clearSlot (slot) {
      const prevKey = slot.assignedWordKey
      if (prevKey) {
        const prevWord = this.currentGroup.wordBank.find((w) => w.wordKey === prevKey)
        if (prevWord) prevWord.placedSlotIndex = null
      }
      slot.assignedWordKey = null
    },

    assignWordToSlot (word, slot) {
      if (this.phase !== 'exercise' || !word || !slot) return

      if (word.placedSlotIndex !== null) {
        const oldSlot = this.currentGroup.slots[word.placedSlotIndex]
        if (oldSlot) oldSlot.assignedWordKey = null
      }

      this.clearSlot(slot)
      slot.assignedWordKey = word.wordKey
      word.placedSlotIndex = slot.slotIndex
      this.selectedWordKey = null
    },

    onWordDragStart (word, event) {
      if (!this.canDragWord()) {
        event.preventDefault()
        return
      }
      this.draggingWordKey = word.wordKey
      this.draggingFromSlotIndex = null
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', word.wordKey)
    },

    onBlankWordDragStart (slot, event) {
      if (this.phase !== 'exercise') {
        event.preventDefault()
        return
      }
      this.draggingWordKey = slot.assignedWordKey
      this.draggingFromSlotIndex = slot.slotIndex
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', slot.assignedWordKey || '')
    },

    onDragEnd () {
      this.draggingWordKey = null
      this.draggingFromSlotIndex = null
    },

    onBlankDragOver (event) {
      if (this.phase === 'exercise') {
        event.dataTransfer.dropEffect = 'move'
      }
    },

    onBlankDrop (slot, event) {
      if (this.phase !== 'exercise') return
      const wordKey = event.dataTransfer.getData('text/plain') || this.draggingWordKey
      const word = this.currentGroup.wordBank.find((w) => w.wordKey === wordKey)
      if (!word) return

      if (this.draggingFromSlotIndex !== null && this.draggingFromSlotIndex !== slot.slotIndex) {
        const fromSlot = this.currentGroup.slots[this.draggingFromSlotIndex]
        const movingWord = this.placedWordForSlot(fromSlot)
        const targetWord = this.placedWordForSlot(slot)
        this.clearSlot(fromSlot)
        this.clearSlot(slot)
        if (movingWord) this.assignWordToSlot(movingWord, slot)
        if (targetWord) this.assignWordToSlot(targetWord, fromSlot)
      } else {
        this.assignWordToSlot(word, slot)
      }
      this.onDragEnd()
    },

    onWordClick (word) {
      if (this.phase !== 'exercise' || word.placedSlotIndex !== null) return
      this.selectedWordKey =
        this.selectedWordKey === word.wordKey ? null : word.wordKey
    },

    onBlankClick (slot) {
      if (this.phase !== 'exercise') return
      if (this.selectedWordKey) {
        const word = this.currentGroup.wordBank.find((w) => w.wordKey === this.selectedWordKey)
        if (word) this.assignWordToSlot(word, slot)
        return
      }
      if (slot.assignedWordKey) {
        this.clearSlot(slot)
      }
    },

    submitCurrentGroup (timedOut = false) {
      this.stopTimer()
      const elapsedSeconds = this.recordGroupElapsed()

      let correct = 0
      this.currentGroup.slots.forEach((slot) => {
        slot.isCorrect = slot.assignedWordKey === slot.itemKey
        if (slot.isCorrect) correct += 1
      })

      this.groupResults.push({
        groupIndex: this.currentGroupIndex,
        correct,
        total: this.currentGroup.slots.length,
        timedOut,
        elapsedSeconds
      })

      this.phase = 'groupResult'
      this.selectedWordKey = null
      this.tagExampleSentenceForWrongSlots(this.currentGroup.slots)
    },

    getStudentIdForTagging () {
      const raw =
        this.payload?.studentId ||
        localStorage.getItem('targetId') ||
        localStorage.getItem('userId')
      const studentId = Number(raw)
      return Number.isFinite(studentId) && studentId > 0 ? studentId : null
    },

    resolveVocabularyIdForSlot (slot) {
      if (slot.vocabularyId != null) return Number(slot.vocabularyId)
      const base = getPrimaryBaseWord(slot.baseWord).toLowerCase()
      const sentence = (slot.sentenceFull || '').trim()
      const item = (this.payload?.items || []).find((entry) => {
        const entryBase = getPrimaryBaseWord(entry.englishText || '').toLowerCase()
        return entryBase === base && (entry.exampleSentence || '').trim() === sentence
      })
      const vocabularyId = Number(item?.vocabularyId ?? item?.vocId)
      return Number.isFinite(vocabularyId) && vocabularyId > 0 ? vocabularyId : null
    },

    tagExampleSentenceForWrongSlots (slots) {
      const exampleTag = Tags.TAGS.find((tag) => tag.chineseName === '消化例句')
      const vocTagId = exampleTag?.id ?? DIGEST_EXAMPLE_SENTENCE_TAG_ID
      const tagName = exampleTag?.chineseName ?? '消化例句'
      const userId = this.getStudentIdForTagging()
      if (!userId) return

      for (const slot of slots) {
        if (slot.isCorrect) continue
        const vocabularyId = this.resolveVocabularyIdForSlot(slot)
        if (!vocabularyId) continue
        if (this.taggedWrongVocabularyIds.has(vocabularyId)) continue
        this.taggedWrongVocabularyIds.add(vocabularyId)

        const postData = {
          vocabularyId,
          userId,
          vocTagId,
          deleted: false
        }
        this.$store
          .dispatch('Vocabulary/tagVocabulary', postData)
          .then((response) => {
            if (response?.data === -1) return
            appendPendingClozeTag({
              vocabularyId,
              userId,
              vocTagId,
              chineseName: tagName
            })
          })
          .catch((error) => console.log(error.toString()))
      }
    },

    goNextGroup () {
      if (this.isLastGroup) {
        this.phase = 'summary'
        this.postStudentClozeTest()
        return
      }
      this.currentGroupIndex += 1
      this.phase = 'exercise'
      this.prepareGroup()
    },

    postStudentClozeTest () {
      if (this.clozeTestSubmitted) return
      const studentId = this.getStudentIdForTagging()
      if (!studentId) {
        ElMessage.warning('未找到学生 ID，无法提交选词填空结果')
        return
      }

      const postData = this.isAssignmentSession
        ? buildVocClozeAssignmentResultPayload({
            payload: this.payload,
            groups: this.groups,
            groupResults: this.groupResults,
            studentId,
            createdBy: Number(localStorage.getItem('userId')) || studentId,
            assignmentId: this.payload.assignmentId,
            isNirvanaSession: this.isNirvanaSession,
            groupSize: this.groupSize,
            timeLimitSeconds: this.timeLimitSeconds,
            totalElapsedSeconds: this.totalElapsedSeconds,
            totalCorrect: this.totalCorrect,
            totalQuestions: this.totalQuestions,
            accuracyPercent: this.accuracyPercent
          })
        : buildVocStudentClozeTestPayload({
            payload: this.payload,
            groups: this.groups,
            groupResults: this.groupResults,
            studentId,
            createdBy: Number(localStorage.getItem('userId')) || studentId,
            isNirvanaSession: this.isNirvanaSession,
            groupSize: this.groupSize,
            timeLimitSeconds: this.timeLimitSeconds,
            totalElapsedSeconds: this.totalElapsedSeconds,
            totalCorrect: this.totalCorrect,
            totalQuestions: this.totalQuestions,
            accuracyPercent: this.accuracyPercent
          })

      this.clozeTestSubmitted = true
      this.$store
        .dispatch(
          this.isAssignmentSession
            ? 'VocClozeAssignmentResult/add'
            : 'VocStudentClozeTest/add',
          postData
        )
        .then(() => {
          ElMessage.success('选词填空结果已提交')
        })
        .catch((err) => {
          this.clozeTestSubmitted = false
          console.error(err)
          const d = err.response?.data
          const firstValidationError = d?.errors
            ? Object.values(d.errors).find((arr) => Array.isArray(arr) && arr.length)?.[0]
            : ''
          const msg =
            (typeof d === 'string' && d) ||
            firstValidationError ||
            d?.message ||
            d?.title ||
            err.message ||
            '选词填空结果上报失败'
          ElMessage.error(String(msg))
        })
    },

    blankClass (slot) {
      return {
        'blank-filled': !!slot.assignedWordKey,
        'blank-selected-target': this.selectedWordKey && this.phase === 'exercise',
        'blank-correct': this.phase === 'groupResult' && slot.isCorrect,
        'blank-wrong': this.phase === 'groupResult' && !slot.isCorrect
      }
    },

    resultRowClass (slot) {
      if (this.phase !== 'groupResult') return {}
      return {
        'row-correct': slot.isCorrect,
        'row-wrong': !slot.isCorrect
      }
    },

    formatDuration (seconds) {
      const m = Math.floor(seconds / 60)
      const s = seconds % 60
      return `${m} 分 ${s} 秒`
    },

    goHome () {
      this.$router.push('/')
    },

    goAssignmentList () {
      this.$router.push({ path: '/clozeassignment' })
    }
  }
}
</script>

<style scoped>
.word-cloze-page {
  min-height: 100vh;
}

.card-header {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #eee;
}

.navi-text {
  margin-left: 1rem;
  color: #666;
  font-size: 0.95rem;
}

.cloze-content {
  max-width: 820px;
  margin: 0 auto;
  padding: 1.5rem;
}

.cloze-panel {
  text-align: center;
  padding: 2rem 1rem;
}

.intro-desc {
  color: #666;
  margin: 1rem 0;
}

.intro-notice {
  text-align: left;
  max-width: 560px;
  margin: 0 auto 1.25rem;
  padding: 0.85rem 1rem;
  background: #fff8f8;
  border: 1px solid #ffcdd2;
  border-radius: 8px;
  color: #555;
  font-size: 0.92rem;
  line-height: 1.65;
}

.intro-notice p {
  margin: 0 0 0.4rem;
  color: #333;
}

.intro-notice ul {
  margin: 0;
  padding-left: 1.2rem;
}

.intro-notice li {
  margin: 0.35rem 0;
}

.intro-red-sample {
  color: #d32f2f;
  font-weight: 700;
}

.intro-config-card {
  max-width: 520px;
  margin: 1.5rem auto 1.75rem;
  padding: 1rem 1.1rem 1.1rem;
  background: linear-gradient(180deg, #f7fcff 0%, #ffffff 100%);
  border: 1px solid #d6f0fa;
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(0, 191, 255, 0.08);
}

.intro-config-grid {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 0.5rem;
  padding-bottom: 1rem;
  margin-bottom: 1rem;
  border-bottom: 1px dashed #cceeff;
}

.intro-stat-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  min-width: 0;
  padding: 0.35rem 0.25rem;
}

.intro-stat-value {
  font-size: 1.75rem;
  line-height: 1.1;
  font-weight: 700;
  color: deepskyblue;
}

.intro-stat-label {
  font-size: 0.82rem;
  color: #667788;
  letter-spacing: 0.02em;
}

.intro-stat-divider {
  width: 1px;
  align-self: stretch;
  background: linear-gradient(
    180deg,
    rgba(0, 191, 255, 0) 0%,
    rgba(0, 191, 255, 0.35) 50%,
    rgba(0, 191, 255, 0) 100%
  );
}

.intro-time-row {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 0.75rem;
  --intro-time-control-height: 36px;
}

.intro-time-row > .intro-time-label-shell,
.intro-time-row > .intro-time-input-shell {
  height: var(--intro-time-control-height);
  min-height: var(--intro-time-control-height);
  max-height: var(--intro-time-control-height);
  box-sizing: border-box;
}

.intro-time-label-shell,
.intro-time-input-shell {
  display: flex;
  align-items: stretch;
  box-sizing: border-box;
  border: 1px solid #b8e6f8;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
  box-shadow: inset 0 1px 2px rgba(0, 191, 255, 0.06);
}

.intro-time-label-shell {
  flex: 1;
  min-width: 0;
}

.intro-time-input-shell {
  flex-shrink: 0;
  min-width: 148px;
}

.intro-time-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: stretch;
  flex-shrink: 0;
  width: 40px;
  background: linear-gradient(180deg, #eef9ff 0%, #e3f5fc 100%);
  border-right: 1px solid #d6f0fa;
  color: #0284c7;
}

.intro-time-text {
  display: flex;
  align-items: center;
  align-self: stretch;
  padding: 0 0.75rem;
  text-align: left;
  min-width: 0;
}

.intro-time-title {
  font-size: 0.9rem;
  font-weight: 700;
  color: #0284c7;
  line-height: 1;
}

.intro-time-field {
  width: 72px;
  height: 100%;
  border: none;
  outline: none;
  text-align: center;
  font-size: 1rem;
  font-weight: 700;
  color: #0284c7;
  background: transparent;
  padding: 0 0.35rem;
  box-sizing: border-box;
  -moz-appearance: textfield;
  appearance: textfield;
}

.intro-time-field::-webkit-outer-spin-button,
.intro-time-field::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.intro-time-locked {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.intro-time-stepper {
  display: flex;
  flex-direction: column;
  width: 26px;
  height: 100%;
  flex-shrink: 0;
  border-left: 1px solid #d6f0fa;
}

.intro-time-step-btn {
  flex: 1 1 0;
  min-height: 0;
  margin: 0;
  padding: 0;
  border: 0;
  border-bottom: 1px solid #d6f0fa;
  background: #f8fdff;
  color: #0284c7;
  cursor: pointer;
  line-height: 1;
  font-size: 0.9rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}

.intro-time-step-btn:last-child {
  border-bottom: 0;
}

.intro-time-step-btn:hover {
  color: deepskyblue;
  background: #eef9ff;
}

.intro-time-suffix {
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: stretch;
  min-width: 36px;
  padding: 0 0.65rem;
  background: linear-gradient(180deg, #eef9ff 0%, #e3f5fc 100%);
  border-left: 1px solid #d6f0fa;
  color: #0284c7;
  font-size: 0.88rem;
  font-weight: 600;
  letter-spacing: 0.04em;
}

.intro-start-btn {
  min-width: 140px;
}

@media (max-width: 520px) {
  .intro-config-grid {
    flex-wrap: wrap;
  }

  .intro-stat-divider {
    display: none;
  }

  .intro-stat-item {
    flex: 1 1 calc(33.33% - 0.5rem);
  }

  .intro-time-row {
    flex-direction: column;
    align-items: stretch;
  }

  .intro-time-label-shell,
  .intro-time-input-shell {
    width: 100%;
    max-width: none;
  }
}

.cloze-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #eee;
}

.group-label {
  font-weight: 600;
  color: deepskyblue;
}

.timer-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #666;
}

.result-banner {
  background: #f0f9ff;
  border: 1px solid #b3e5fc;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  text-align: center;
  color: #0277bd;
  font-weight: 600;
}

.word-bank-section,
.sentences-section {
  margin-bottom: 1.5rem;
}

.word-bank-section h3,
.sentences-section h3 {
  font-size: 1rem;
  color: #333;
  margin-bottom: 0.75rem;
}

.word-bank {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
}

.word-chip {
  padding: 0.45rem 1rem;
  border: 2px solid deepskyblue;
  border-radius: 999px;
  background: #fff;
  color: deepskyblue;
  cursor: grab;
  user-select: none;
  font-weight: 600;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.word-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 191, 255, 0.25);
}

.word-chip-used {
  opacity: 0.45;
}

.word-chip-hidden {
  visibility: hidden;
  pointer-events: none;
}

.click-hint {
  margin-top: 0.5rem;
  font-size: 0.85rem;
  color: #888;
}

.sentence-row {
  margin-bottom: 1rem;
  padding: 0.85rem 1rem;
  border-radius: 8px;
  background: #fafafa;
  border: 1px solid #eee;
}

.sentence-row.row-correct {
  border-color: #81c784;
  background: #f1f8e9;
}

.sentence-row.row-wrong {
  border-color: #ef9a9a;
  background: #fff5f5;
}

.sentence-line {
  margin: 0;
  line-height: 1.8;
  font-size: 1.05rem;
}

.cloze-blank {
  display: inline-block;
  min-width: 6rem;
  margin: 0 0.2rem;
  padding: 0.1rem 0.5rem;
  border-bottom: 2px dashed #bbb;
  vertical-align: baseline;
  text-align: center;
  cursor: pointer;
}

.cloze-blank.blank-selected-target {
  border-bottom-color: deepskyblue;
  background: rgba(0, 191, 255, 0.08);
}

.cloze-blank.blank-filled {
  border-bottom-style: solid;
  border-bottom-color: deepskyblue;
}

.cloze-blank.blank-correct {
  border-bottom-color: #43a047;
  background: rgba(67, 160, 71, 0.1);
}

.cloze-blank.blank-wrong {
  border-bottom-color: #e53935;
  background: rgba(229, 57, 53, 0.08);
}

.blank-placeholder {
  color: #aaa;
  letter-spacing: 2px;
}

.blank-filled-word {
  color: deepskyblue;
  font-weight: 700;
  cursor: grab;
}

.blank-result-form {
  cursor: default;
}

.form-change-part {
  color: #d32f2f;
  font-weight: 800;
  background: rgba(211, 47, 47, 0.12);
  border-radius: 3px;
  padding: 0 2px;
  box-decoration-break: clone;
  -webkit-box-decoration-break: clone;
}

.result-detail {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.5rem;
  font-size: 0.9rem;
}

.icon-correct {
  color: #43a047;
}

.icon-wrong {
  color: #e53935;
}

.correct-answer-hint {
  color: #555;
  font-size: 0.9rem;
}

.correct-answer-hint strong {
  color: #0277bd;
}

.cloze-actions {
  text-align: center;
  margin-top: 1.5rem;
}

.summary-page {
  text-align: left;
}

.summary-header {
  text-align: center;
  padding-bottom: 1.25rem;
  margin-bottom: 1.25rem;
  border-bottom: 1px solid #eee;
}

.summary-group-section {
  margin-bottom: 1.75rem;
}

.summary-group-title {
  font-size: 1.05rem;
  color: deepskyblue;
  margin: 0 0 0.75rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.summary-group-score {
  font-size: 0.9rem;
  font-weight: 600;
  color: #666;
}

.slot-word-label {
  margin: 0 0 0.35rem;
}

.slot-base-word {
  display: inline-block;
  padding: 0.15rem 0.65rem;
  border: 1px solid deepskyblue;
  border-radius: 999px;
  color: deepskyblue;
  font-size: 0.85rem;
  font-weight: 600;
}

.summary-score {
  font-size: 1.4rem;
  font-weight: 700;
  color: deepskyblue;
  margin: 1rem 0;
}

.summary-time {
  color: #666;
  margin-bottom: 1.5rem;
}

.summary-actions {
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #eee;
}
</style>
